import { Button, makeStyles } from "@fluentui/react-components";
import { WarningCircle } from "@phosphor-icons/react";

import { tokens } from "../../theme";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================
const t = {
    // Spacing
    spacingVerticalS: tokens.primitives["spacing-Vertical-S"],
    spacingVerticalM: tokens.primitives["spacing-Vertical-M"],
    spacingHorizontalS: tokens.primitives["spacing-Horizontal-S"],
    spacingHorizontalM: tokens.primitives["spacing-Horizontal-M"],
    spacingHorizontalL: tokens.primitives["spacing-Horizontal-L"],

    // Typography
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontSizeBase400: tokens.primitives["font-Size-Base-400"],
    fontWeightRegular: tokens.primitives["font-Weight-Regular"],
    lineHeightBase400: tokens.primitives["lineHeight-Base-400"],

    // Colors
    neutralPrimary: tokens.semantic["neutral-Primary"],
    neutralWhite: tokens.semantic["neutral-White"],
    dangerForeground: tokens.semantic["statusDangerForegroundPrimary"],
    dangerBackground: tokens.semantic["statusDangerBackgroundSecondary"],
    dangerStroke: tokens.semantic["statusDangerStrokePrimary"],

    // Radius
    radiusLarge: tokens.primitives["stroke-Radius-Large"],
    radiusCircular: tokens.primitives["stroke-Radius-Circular"],

    // Shadows
    shadowRestingXSmall: tokens.shadows["shadow-Resting-Xs"],

    // Stroke
    strokeFocus: tokens.primitives.strokeFocus
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    wrapper: {
        display: "flex",
        justifyContent: "flex-start",
        width: "100%"
    },

    container: {
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: t.spacingHorizontalM,
        width: "100%",
        maxWidth: "637px",
        backgroundColor: t.dangerBackground,
        border: `1px solid ${t.dangerStroke}`,
        borderRadius: t.radiusLarge,
        padding: t.spacingHorizontalL,
        boxSizing: "border-box",
        boxShadow: t.shadowRestingXSmall
    },

    iconContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "40px",
        height: "40px",
        borderRadius: t.radiusCircular,
        backgroundColor: t.dangerForeground,
        color: t.neutralWhite,
        flexShrink: 0,
        "@media (max-width: 768px)": {
            width: "24px",
            height: "24px"
        }
    },

    content: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        gap: t.spacingVerticalM
    },

    errorText: {
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase400,
        fontWeight: t.fontWeightRegular as unknown as number,
        lineHeight: t.lineHeightBase400,
        color: t.neutralPrimary,
        margin: 0
    },

    retryButton: {
        alignSelf: "flex-start"
    }
});

// =============================================================================
// COMPONENT
// =============================================================================

interface Props {
    error: string;
    onRetry: () => void;
}

export const AnswerError = ({ error, onRetry }: Props) => {
    const styles = useStyles();

    return (
        <div className={styles.wrapper} role="alert" aria-live="assertive">
            <div className={styles.container}>
                <div className={styles.iconContainer} aria-hidden="true">
                    <WarningCircle size={26} weight="fill" />
                </div>

                <div className={styles.content}>
                    <p className={styles.errorText}>{error}</p>

                    <Button appearance="primary" className={styles.retryButton} onClick={onRetry}>
                        Retry
                    </Button>
                </div>
            </div>
        </div>
    );
};
